/**
 * Drafting an invoice.
 *
 * A draft is freely editable (`draft_invoice()` in `domains/finance/services.py`), so
 * this dialog asks only for what has to be decided up front — who it is billed to, and
 * which matter it belongs to, if any — and creates an empty draft. Lines are added on
 * the invoice detail page, which is the anchor screen for everything else an invoice
 * needs (issuing, sending, credit notes, payments). Asking for line items here as well
 * would duplicate that screen rather than lead to it.
 *
 * Mirrors `ConvertLeadDialog`: a modal reached from the list, not a dedicated route,
 * because there is nothing here to review before acting — unlike conversion, drafting
 * an invoice is cheap to undo (it is a draft) and there is no plan to show first.
 */

import {
  STAFF,
  qk,
  useApi,
  useApiMutation,
  useApiQuery,
  type CaseListItem,
  type ClientListItem,
  type CursorPage,
  type Invoice,
} from '@legalcrm/api';
import { Form, FormActions, FormDate, FormInput, FormSelect, Modal, useZodForm } from '@legalcrm/ui';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { useAuth } from '@/auth/AuthProvider';

const schema = z.object({
  client: z.string().min(1, 'Choose a client'),
  case: z.string(),
  currency: z
    .string()
    .trim()
    .min(1, 'Enter a three-letter currency code')
    .max(3, 'A three-letter currency code'),
  due_date: z.string(),
});

type Values = z.infer<typeof schema>;

export function NewInvoiceDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const api = useApi();
  const navigate = useNavigate();
  const { baseCurrency } = useAuth();

  // The firm's own currency, not a literal. A PKR firm was pre-filled with USD on every
  // new invoice, and the easiest mistake in billing is the one the form makes for you.
  const form = useZodForm(schema, {
    defaultValues: { client: '', case: '', currency: baseCurrency, due_date: '' },
  });

  // Cursor-paginated on the live endpoint; the first page is what a firm-sized picker
  // needs in practice, matching the same simplification `NewCasePage` already makes.
  const clients = useApiQuery<CursorPage<ClientListItem>>(qk.list('clients'), STAFF.clients, {
    enabled: open,
  });

  const chosenClientId = form.watch('client');
  // Narrowed to the chosen client, so the list is not every matter in the firm. Cleared
  // automatically by `key` below when the client changes.
  const cases = useApiQuery<CursorPage<CaseListItem>>(
    qk.list('cases', { client: chosenClientId }),
    STAFF.cases,
    { query: { client: chosenClientId }, enabled: open && Boolean(chosenClientId) },
  );

  const create = useApiMutation<Invoice, Values>({
    mutationFn: (values) =>
      api.post<Invoice>(STAFF.invoices, {
        client: values.client,
        case: values.case || undefined,
        currency: values.currency.toUpperCase(),
        due_date: values.due_date || undefined,
        lines: [],
      }),
    invalidates: [qk.all('invoices')],
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Draft an invoice"
      description="Who it is billed to. Lines are added on the invoice itself."
    >
      <Form
        form={form}
        ariaLabel="Draft an invoice"
        error={create.error}
        onSubmit={async (values) => {
          const invoice = await create.mutateAsync(values);
          form.reset();
          onClose();
          navigate(`/invoices/${invoice.id}`);
        }}
      >
        <FormSelect
          form={form}
          name="client"
          label="Client"
          placeholder={clients.isLoading ? 'Loading clients…' : 'Choose a client'}
          options={(clients.data?.results ?? []).map((client) => ({
            value: client.id,
            label: client.display_name || client.reference,
          }))}
        />

        {chosenClientId && (
          <FormSelect
            key={chosenClientId}
            form={form}
            name="case"
            label="Matter"
            placeholder={cases.isLoading ? 'Loading matters…' : 'None — billed to the client directly'}
            hint="Optional. An invoice does not have to belong to one matter."
            options={(cases.data?.results ?? []).map((matter) => ({
              value: matter.id,
              label: `${matter.case_number} — ${matter.title}`,
            }))}
          />
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <FormInput
            form={form}
            name="currency"
            label="Currency"
            hint={`Three-letter code. Your firm bills in ${baseCurrency}.`}
            maxLength={3}
          />
          <FormDate form={form} name="due_date" label="Due date" hint="Optional." />
        </div>

        <FormActions submitLabel="Create draft" busy={create.isPending} onCancel={onClose} />
      </Form>
    </Modal>
  );
}
